import type { AuthUser } from "../context/AuthContext";
import GRPC_BACKEND_URL from "../grpcConfig";

export type PushSupport = "supported" | "install-required" | "unsupported";

export class PushUnavailableError extends Error {}

const apiUrl = (path: string) => new URL(`/push/${path}`, GRPC_BACKEND_URL).href;
const headersFor = (user: AuthUser, json = false): HeadersInit => ({
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(user.token ? { Authorization: `Bearer ${user.token}` } : {}),
});
const credentialsFor = (user: AuthUser): RequestCredentials => user.token ? "same-origin" : "include";

export const getPushSupport = (): PushSupport => {
    if (typeof window === "undefined" || typeof navigator === "undefined") return "unsupported";
    const ua = navigator.userAgent;
    const ios = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
    const installed = window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (ios && !installed) return "install-required";
    return navigator.serviceWorker !== undefined && "PushManager" in window && typeof Notification !== "undefined" ? "supported" : "unsupported";
};

export const loadPushPreferences = async (user: AuthUser): Promise<{ enabled: boolean; recentWindowMinutes: number }> => {
    const response = await fetch(apiUrl("preferences"), { headers: headersFor(user), credentials: credentialsFor(user) });
    if (response.status === 404) throw new PushUnavailableError("Push preferences unavailable");
    if (!response.ok) throw new Error("Push preferences unavailable");
    return response.json();
};

const savePreferences = async (user: AuthUser, enabled: boolean, recentWindowMinutes = 180) => {
    const response = await fetch(apiUrl("preferences"), {
        method: "PUT",
        headers: headersFor(user, true),
        credentials: credentialsFor(user),
        body: JSON.stringify({ enabled, recentWindowMinutes, localeHint: navigator.language.slice(0, 16) }),
    });
    if (!response.ok) throw new Error("Push preferences could not be updated");
};

const sendSubscription = async (user: AuthUser, subscription: PushSubscription, method: "POST" | "DELETE", continueAfterDeleteFailure = false): Promise<boolean> => {
    const body = method === "POST" ? { endpoint: subscription.endpoint, ...subscription.toJSON().keys } : { endpoint: subscription.endpoint };
    const response = await fetch(apiUrl("subscription"), {
        method,
        headers: headersFor(user, true),
        credentials: credentialsFor(user),
        body: JSON.stringify(body),
    });
    if (response.status === 404) throw new PushUnavailableError("Push is not configured");
    if (!response.ok) {
        if (continueAfterDeleteFailure && method === "DELETE" && (response.status === 401 || response.status === 403 || response.status === 429 || response.status >= 500)) {
            await subscription.unsubscribe().catch(() => undefined);
            return false;
        }
        throw new Error("Push subscription could not be updated");
    }
    return true;
};

const decodeVapidKey = (key: string) => {
    const padded = key.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(key.length / 4) * 4, "=");
    return Uint8Array.from(window.atob(padded), (character) => character.codePointAt(0)!);
};

export const enablePush = async (user: AuthUser, recentWindowMinutes = 180): Promise<void> => {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error(permission === "denied" ? "Push permission denied" : "Push permission was not granted");

    const configResponse = await fetch(apiUrl("config"), { credentials: "omit" });
    if (!configResponse.ok) throw new Error("Push is not configured");
    const { publicKey } = await configResponse.json();
    if (typeof publicKey !== "string" || !publicKey) throw new Error("Push is not configured");

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) throw new Error("Service worker is unavailable");
    const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: decodeVapidKey(publicKey) });
    try {
        await sendSubscription(user, subscription, "POST");
        await savePreferences(user, true, recentWindowMinutes);
    } catch (error) {
        await sendSubscription(user, subscription, "DELETE").catch(() => undefined);
        await subscription.unsubscribe();
        throw error;
    }
};

export const disablePush = async (user: AuthUser, recentWindowMinutes = 180): Promise<void> => {
    await savePreferences(user, false, recentWindowMinutes);
    if (navigator.serviceWorker !== undefined && "PushManager" in window) {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) await subscription.unsubscribe();
    }
};

export class PushLogoutOfflineError extends Error {
    constructor() {
        super("Push logout requires a connection");
        this.name = "PushLogoutOfflineError";
    }
}

export const removePushBeforeLogout = async (user: AuthUser): Promise<void> => {
    if (navigator.serviceWorker === undefined || !("PushManager" in window)) return;
    let registration: ServiceWorkerRegistration | undefined;
    let subscription: PushSubscription | null;
    try {
        registration = await navigator.serviceWorker.getRegistration();
        subscription = registration ? await registration.pushManager.getSubscription() : null;
    } catch (error) {
        if (!navigator.onLine || error instanceof TypeError) throw new PushLogoutOfflineError();
        throw new Error("Push subscription could not be checked");
    }
    if (!subscription) return;
    try {
        const removed = await sendSubscription(user, subscription, "DELETE", true);
        if (!removed) return;
    } catch (error) {
        if (error instanceof PushUnavailableError) {
            await subscription.unsubscribe().catch(() => undefined);
            return;
        }
        if (!navigator.onLine || error instanceof TypeError) throw new PushLogoutOfflineError();
        throw new Error("Push subscription could not be removed");
    }
    await subscription.unsubscribe();
};
